/* eslint react-hooks/immutability: ["warn"] */
import { useCallback, useRef, useState } from "react";
import Webcam from "react-webcam";
import { useUserMediaErrorHandler } from "../../hooks/useUserMediaErrorHandler";
import { useVideoLoadedDataHandler } from "../../hooks/useVideoLoadedDataHandler";
import { useVideoPlayingHandler } from "../../hooks/useVideoPlayingHandler";
import { useBarcodeAnimationEffect } from "../../hooks/useBarcodeAnimationEffect";
import { useResetVideoReadyOnConstraintsEffect } from "../../hooks/useResetVideoReadyOnConstraintsEffect";
import { useDetectBarcode, ScanResult } from "../../hooks/useDetectBarcode";
import { TaskType } from "@coworker/apprestructured/src/tasks/enums/taskTypes";
import CameraScannerPagePresentation from "./CameraScannerPagePresentation";

type CameraScannerContainerProps = {
  onDetected: (scanResult: ScanResult) => void;
  presentationProps: {
    taskType: TaskType;
    pushSearch: () => void;
  };
};

export function CameraScannerComponent({
  onDetected,
  presentationProps,
}: CameraScannerContainerProps) {
  const webcamRef = useRef<Webcam>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const [videoConstraints, setVideoConstraints] =
    useState<MediaTrackConstraints>({
      facingMode: { exact: "environment" },
    });
  const [errorState, setErrorState] = useState<string | null>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  const handleUserMediaError = useUserMediaErrorHandler({
    setIsVideoReady,
    setErrorState,
    setVideoConstraints,
  });

  const handleVideoLoadedData = useVideoLoadedDataHandler({
    webcamRef,
    setIsVideoReady,
    setErrorState,
  });

  const handleVideoPlaying = useVideoPlayingHandler({ setIsVideoReady });

  const detectBarcode = useDetectBarcode({
    isVideoReady,
    webcamRef,
    onDetected,
  });

  // Animation loop for continuous detection
  const tick = useCallback(() => {
    detectBarcode();
    animationRef.current = requestAnimationFrame(tick);
  }, [detectBarcode]);

  useBarcodeAnimationEffect({
    isVideoReady,
    tick,
    animationRef,
  });

  useResetVideoReadyOnConstraintsEffect({
    videoConstraints,
    setIsVideoReady,
  });

  const videoComponent = (
    <Webcam
      audio={false}
      ref={webcamRef}
      screenshotFormat="image/jpeg"
      videoConstraints={videoConstraints}
      style={{ width: "100%", height: "100%" }}
      onUserMediaError={handleUserMediaError}
      onLoadedData={handleVideoLoadedData}
      onPlaying={handleVideoPlaying}
    />
  );

  return (
    <CameraScannerPagePresentation
      videoComponent={videoComponent}
      error={errorState}
      setError={setErrorState}
      taskType={presentationProps.taskType}
      pushSearch={presentationProps.pushSearch}
    />
  );
}
